/**
 * The sourcing (Phase 6, Task 20) slice of the wire — candidate discovery runs.
 *
 * Hand-written to mirror `openapi.yaml`'s `Sourcing` tag, exactly as `outreach.ts`'s
 * own header describes for its slice: transcribe what the screen reads, not the whole
 * contract, and this task does not touch `openapi.yaml`. A field the contract marks
 * `nullable` is `T | null` here, never omitted; a field a request marks optional is
 * optional here, because `exactOptionalPropertyTypes` is on and an absent value and an
 * explicit `null` are different facts (`PRODUCT.md` §4).
 *
 * Nothing here authorises anything. `GET /sourcing/runs` is `recruiting.view_candidates`
 * at scope `company` — the same authoriser the candidates surface uses for its reads,
 * because a run lists the leads it found, which are candidate-shaped. `POST
 * /sourcing/runs` is `recruiting.manage_jobs` at scope `company` — the same authoriser
 * the requisitions surface uses for its writes, because triggering a discovery run is a
 * decision about a requisition's hiring pipeline. The server runs that chain on every
 * call, whatever this screen renders.
 */

import { qs, type QueryValue } from './paths';

/* ──────────────────────────────────────────────────────────────── channels ── */

/**
 * `SourcingChannel` of `openapi.yaml`, verbatim — the nine channels
 * `src/modules/sourcing/adapter.ts`'s closed `SOURCING_CHANNELS` set names. The first
 * four ship enabled by default; the last five are QUARANTINED (see
 * `web/src/recruiting/sourcing.ts` for the split and the words this screen puts on it).
 */
export const SOURCING_CHANNELS = [
  // low-risk — usable by default
  'career_page',
  'ats_import',
  'email',
  'job_board_api',
  // high-risk — QUARANTINED, never selectable from this screen
  'linkedin_dm',
  'linkedin_comment',
  'facebook_dm',
  'facebook_comment',
  'web_scrape',
] as const;

export type SourcingChannel = (typeof SOURCING_CHANNELS)[number];

/* ──────────────────────────────────────────────────────────────────── runs ── */

/** `SourcingRunStatus` of `openapi.yaml`, verbatim. */
export const SOURCING_RUN_STATUSES = ['pending', 'running', 'completed', 'failed'] as const;

export type SourcingRunStatus = (typeof SOURCING_RUN_STATUSES)[number];

/**
 * `SourcingRun` of `openapi.yaml`. `required` lists every member — every field is
 * always present on the wire, and the nullable ones are `T | null` rather than
 * optional because the server sends the key with a `null` value, it does not omit it.
 * `requisition_id` is nullable because a later scheduled sweep may run company-wide
 * rather than against one requisition; every run this screen can trigger carries one.
 */
export interface SourcingRun {
  readonly id: string;
  readonly company_id: string;
  readonly requisition_id: string | null;
  readonly channel: SourcingChannel;
  readonly status: SourcingRunStatus;
  readonly leads_found: number;
  readonly leads_new: number;
  readonly error_message: string | null;
  readonly started_at: string | null;
  readonly finished_at: string | null;
  readonly created_at: string;
}

/** `GET /sourcing/runs` — a bare `{ data: [...] }`, newest first, no `page`. */
export interface SourcingRunListResponse {
  readonly data: readonly SourcingRun[];
}

export const SOURCING_RUNS_PATH = '/api/v1/sourcing/runs';

/**
 * `GET /sourcing/runs?requisition_id=&limit=` — newest first. `requisition_id` is
 * optional; omitted, the read is company-wide rather than narrowed to one
 * requisition. `limit` 1..500, default 100.
 */
export function sourcingRunsPath(query: {
  readonly requisitionId?: string | undefined;
  readonly limit?: number | undefined;
}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    requisition_id: query.requisitionId,
    limit: query.limit,
  };
  return `${SOURCING_RUNS_PATH}${qs(params)}`;
}

/**
 * `SourcingRunTriggerRequest` — `requisition_id` and `channel` are both required.
 * There is no optional field: a trigger is always against one requisition and one
 * channel, never a batch.
 */
export interface SourcingRunTriggerRequest {
  readonly requisition_id: string;
  readonly channel: SourcingChannel;
}

/**
 * `SourcingRunTriggerResponse` — the completed (or failed) run, `POST /sourcing/runs`
 * running the discovery synchronously rather than queuing it. Deliberately **not**
 * the same shape as `SourcingRun`: this is the summary of what the trigger itself did
 * (`candidates_created`, `failed`), not a second read of the row — the screen reloads
 * `GET /sourcing/runs` for that, the same "the write's own response is a receipt, not
 * a re-read" discipline `outreach.ts` follows for its own creates.
 */
export interface SourcingRunTriggerResponse {
  readonly id: string;
  readonly channel: SourcingChannel;
  readonly leads_found: number;
  readonly leads_new: number;
  readonly candidates_created: number;
  /** `true` when the adapter's discovery step threw — the run is still recorded, never lost. */
  readonly failed: boolean;
}
